import { takeEvery, takeLatest } from 'redux-saga'
import { call, put, select } from 'redux-saga/effects'
import fetcher from 'modules/fetcher'
import { getProjectSelector } from 'Projects/selectors'

import {
  projectFetchError,
  projectFetchSuccess,
  loadProjectFiles
} from 'Projects/actions'

import {
  PROJECT_CREATE,
  PROJECT_PROFILE_FETCH,
  PROJECT_FETCH,
  PROJECT_UPDATE,
  PROJECT_ARCHIVE,
  PROJECT_UNARCHIVE,
  PROJECT_DELETE,
  PROJECT_FETCH_FILES
} from 'Projects/constants'

function * createProjectSaga ({ payload, successCallback, failureCallback }) {
  try {
    const response = yield call(
      fetcher,
      '/api/v3/projects', {
        body: JSON.stringify({ project: { ...payload } }),
        method: 'POST'
      }
    )

    yield put(projectFetchSuccess(response))

    if (successCallback) {
      yield call(successCallback, response)
    }
  } catch (exception) {
    console.error(exception.message)

    if (failureCallback) {
      yield call(failureCallback, exception)
    }
  }
}

function * fetchProjectSaga ({ payload }) {
  try {
    if (payload.window && payload.window.id === payload.projectId) {
      yield put(projectFetchSuccess(payload.window))
    } else {
      const response = yield call(
        fetcher,
        `/api/v3/projects/${payload.projectId}`, {
          method: 'GET'
        }
      )
      yield put(projectFetchSuccess(response))
    }
  } catch (exception) {
    console.error(exception.message)
    const errorBody = yield exception.response.json()
    yield put(projectFetchError(errorBody.error))
  }
}

function * updateProjectSaga ({ payload, successCallback, failureCallback, finallyCallback }) {
  const project = yield select(getProjectSelector)
  const projectId = payload.id || project.id

  try {
    const response = yield call(
      fetcher,
      `/api/v3/projects/${projectId}`, {
        body: JSON.stringify({ project: { ...payload } }),
        method: 'PATCH'
      }
    )

    yield put(projectFetchSuccess(response))
    if (successCallback) {
      yield call(successCallback, response)
    }
  } catch (exception) {
    console.error(exception.message)

    if (failureCallback) {
      yield call(failureCallback, exception)
    }
  } finally {
    if (finallyCallback) {
      yield call(finallyCallback)
    }
  }
}

function * archiveProjectSaga ({ payload, successCallback, failureCallback }) {
  try {
    const response = yield call(
      fetcher,
      `/api/v3/projects/${payload.id}/archive`, {
        method: 'PUT'
      }
    )

    yield put(projectFetchSuccess(response))
    if (successCallback) {
      yield call(successCallback, response)
    }
  } catch (exception) {
    console.error(exception.message)

    if (failureCallback) {
      yield call(failureCallback, exception)
    }
  }
}

function * unarchiveProjectSaga ({ payload, successCallback, failureCallback }) {
  try {
    const response = yield call(
      fetcher,
      `/api/v3/projects/${payload.id}/unarchive`, {
        method: 'PUT'
      }
    )

    yield put(projectFetchSuccess(response))
    if (successCallback) {
      yield call(successCallback, response)
    }
  } catch (exception) {
    console.error(exception.message)

    if (failureCallback) {
      yield call(failureCallback, exception)
    }
  }
}

function * deleteProjectSaga ({ payload, successCallback, failureCallback }) {
  try {
    yield call(
      fetcher,
      `/api/v3/projects/${payload.id}`, {
        method: 'DELETE'
      }
    )

    if (successCallback) {
      yield call(successCallback)
    }
  } catch (exception) {
    console.error(exception.message)

    if (failureCallback) {
      yield call(failureCallback, exception)
    }
  }
}

function * watchCreateProjectSaga () {
  yield call(takeEvery, PROJECT_CREATE, createProjectSaga)
}

function * watchFetchProjectSaga () {
  yield call(takeEvery, [PROJECT_PROFILE_FETCH, PROJECT_FETCH], fetchProjectSaga)
}

function * watchUpdateProjectSaga () {
  yield call(takeEvery, PROJECT_UPDATE, updateProjectSaga)
}

function * watchArchiveProjectSaga () {
  yield call(takeEvery, PROJECT_ARCHIVE, archiveProjectSaga)
}

function * watchUnarchiveProjectSaga () {
  yield call(takeEvery, PROJECT_UNARCHIVE, unarchiveProjectSaga)
}

function * watchDeleteProjectSaga () {
  yield call(takeEvery, PROJECT_DELETE, deleteProjectSaga)
}

function * fetchProjectFilesSaga ({ payload }) {
  const project = yield select(getProjectSelector)
  const projectId = (payload && payload.projectId) || project.id

  try {
    const response = yield call(
      fetcher,
      `/api/internal/projects/${projectId}/files`, {
        method: 'GET'
      }
    )
    yield put(loadProjectFiles(response))
  } catch (exception) {
    console.error(exception.message)
  }
}

function * watchFetchProjectFilesSaga () {
  yield call(takeLatest, [PROJECT_PROFILE_FETCH, PROJECT_FETCH_FILES], fetchProjectFilesSaga)
}

export const projectProfileSagas = [
  watchCreateProjectSaga(),
  watchFetchProjectSaga(),
  watchUpdateProjectSaga(),
  watchArchiveProjectSaga(),
  watchUnarchiveProjectSaga(),
  watchDeleteProjectSaga(),
  watchFetchProjectFilesSaga()
]
